import type { AggregatedMwrrItem } from '@/api/types';

interface AggregatedMwrrChartDataItem extends AggregatedMwrrItem {
    name: string;
    chartValue: number | null;
}

interface AggregatedReturnsTooltipProps {
    active?: boolean;
    payload?: Array<{
        payload: AggregatedMwrrChartDataItem;
    }>;
}

/**
 * Custom tooltip for the aggregated returns chart (MWRR).
 * Displays the return percentage and ledger count.
 */
export function AggregatedReturnsTooltip({ active, payload }: AggregatedReturnsTooltipProps) {
    if (!active || !payload || payload.length === 0 || !payload[0]) {
        return null;
    }

    const data = payload[0].payload;
    const returnValue = data.mwrr;

    // Determine color based on value
    const getValueColor = (value: number | null) => {
        if (value === null) return 'text-gray-400 dark:text-gray-500';
        return value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    };

    const formatValue = (value: number | null) => {
        if (value === null) return 'N/A';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}%`;
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2 sm:p-3 text-sm">
            {/* Period label */}
            <div className="font-medium text-gray-900 dark:text-white text-xs sm:text-sm mb-1 sm:mb-2">
                {data.periodLabel}
            </div>

            {/* Desktop: labeled row */}
            <div className="hidden sm:flex items-center justify-between gap-4">
                <span className="text-gray-500 dark:text-gray-400">MWRR:</span>
                <span className={`font-semibold ${getValueColor(returnValue)}`}>
                    {formatValue(returnValue)}
                </span>
            </div>

            {/* Mobile: vertical stacked layout with label */}
            <div className="sm:hidden">
                <div className="text-xs text-gray-500 dark:text-gray-400">MWRR</div>
                <div className={`font-semibold ${getValueColor(returnValue)}`}>
                    {formatValue(returnValue)}
                </div>
            </div>

            {returnValue === null && (
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 sm:mt-2 sm:pt-2 sm:border-t sm:border-gray-200 sm:dark:border-gray-700">
                    Requires 3+ months of data
                </div>
            )}
        </div>
    );
}
